import type { FavoriteEntityType, UserFavorite } from '@server/types';
import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';

interface UseStarredOptions {
  enabled?: boolean;
}

interface StarredListResult {
  items: UserFavorite[];
}

interface ToggleStarInput {
  entityType: FavoriteEntityType;
  entityId: string;
  starred: boolean;
}

export function useStarred(entityType: FavoriteEntityType, options: UseStarredOptions = {}) {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const queryOptions = orpc.favorites.listStarred.queryOptions({ input: { entityType } });
  const favoriteMutationKey = useMemo(() => ['favorites', entityType] as const, [entityType]);

  const query = useQuery({
    ...queryOptions,
    enabled: options.enabled ?? true,
  });

  const pendingEntityIds = useMutationState<string | null>({
    filters: { mutationKey: favoriteMutationKey, status: 'pending' },
    select: (pendingMutation) => {
      const variables = pendingMutation.state.variables as ToggleStarInput | undefined;
      return variables?.entityId ?? null;
    },
  });
  const pendingIdSet = useMemo(
    () => new Set(pendingEntityIds.filter((id): id is string => id !== null)),
    [pendingEntityIds],
  );

  const mutation = useMutation(
    orpc.favorites.toggleStar.mutationOptions({
      mutationKey: favoriteMutationKey,
      scope: { id: `favorites:${entityType}` },
      onMutate: async (input: ToggleStarInput) => {
        await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });

        queryClient.setQueryData<StarredListResult>(queryOptions.queryKey, (current) => {
          const existing = current ?? { items: [] };
          if (input.starred) {
            if (existing.items.some((item) => item.entityId === input.entityId)) return existing;
            return { items: [{ entityId: input.entityId, createdAt: new Date().toISOString() }, ...existing.items] };
          }
          return { items: existing.items.filter((item) => item.entityId !== input.entityId) };
        });
      },
      onError: (error, input) => {
        queryClient.setQueryData<StarredListResult>(queryOptions.queryKey, (current) => {
          const items = current?.items ?? [];
          if (input.starred) {
            return { items: items.filter((item) => item.entityId !== input.entityId) };
          }
          if (items.some((item) => item.entityId === input.entityId)) {
            return { items };
          }
          return { items: [{ entityId: input.entityId, createdAt: new Date().toISOString() }, ...items] };
        });
        toast.error(
          error instanceof Error ? error.message : `Failed to ${input.starred ? 'star' : 'unstar'} dashboard`,
        );
      },
      onSettled: () => {
        if (queryClient.isMutating({ mutationKey: favoriteMutationKey }) === 1) {
          void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        }
      },
    }),
  );

  const items = useMemo(() => query.data?.items ?? [], [query.data?.items]);
  const starredIds = useMemo(() => new Set(items.map((item) => item.entityId)), [items]);
  const isStarred = useCallback((id: string) => starredIds.has(id), [starredIds]);
  const toggleStar = useCallback(
    (id: string, starred: boolean) => {
      if (pendingIdSet.has(id)) return;
      mutation.mutate({ entityType, entityId: id, starred });
    },
    [entityType, mutation, pendingIdSet],
  );
  const isToggling = useCallback((id: string) => pendingIdSet.has(id), [pendingIdSet]);

  return {
    items,
    starredIds,
    isStarred,
    toggleStar,
    isToggling,
    isLoading: query.isLoading,
    isPending: mutation.isPending,
  };
}
